import { python } from '@codemirror/lang-python'
import { EditorState } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import { basicSetup } from 'codemirror'
import { marked } from 'marked'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'

import { withTranslation } from 'react-i18next'
import {
  getRunnableCodeForStep,
  getUserCodeForStep,
  populateSelections,
} from '../../../helpers/codeParsing'
import {
  saveToLocalStorage,
  loadFromLocalStorage,
} from '../../../helpers/localStorage'
import validator from '../../../helpers/validator'
import Input from '../Input/Input'
import RunBar from '../RunBar/RunBar'
import Editor from '../Editor/Editor'
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import './Step.scss'

const Step = ({
  exerciseId,
  instructions,
  codeTemplate,
  options,
  onNextStep,
  onStepRun,
  completed,
  stepIndex,
  isLastStep,
  t,
}) => {
  const [codeToRun, setCodeToRun] = useState('')
  const [displayCode, setDisplayCode] = useState('')

  const editorRef = useRef(null)

  useEffect(() => {
    if (!editorRef.current) return

    const initialState = EditorState.create({
      doc: codeToRun,
      extensions: [basicSetup, python()],
    })

    const view = new EditorView({
      state: initialState,
      parent: editorRef.current,
    })

    return () => {
      view.destroy()
    }
  }, [codeTemplate, codeToRun])

  useEffect(() => {
    const savedSelections = loadFromLocalStorage('selections')
    if (savedSelections) {
      setCodeToRun(
        populateSelections(
          getRunnableCodeForStep({ template: codeTemplate, stepIndex }),
          savedSelections
        )
      )
    }
  }, [codeTemplate, stepIndex])

  /**
   * Accepts the provided user code into the code template, including hidden code for execution
   * but without the step templates
   * @param {string} userCode display code that the user sees
   */
  const onUserCodeUpdate = useCallback(() => {
    const runnableCode = getRunnableCodeForStep({
      template: codeTemplate,
      stepIndex,
    })
    setCodeToRun(
      populateSelections(runnableCode, loadFromLocalStorage('selections'))
    )
    saveToLocalStorage({ runnableCode })
  }, [codeTemplate, stepIndex])

  const [error, setError] = useState({ type: false, description: false })

  useEffect(() => {
    const handleRunStarted = () => {
      setError({})
    }

    const handleRunCompleted = ({
      detail: {
        errorDetails: { type, description },
      },
    }) => {
      description = description?.replace(/^./, description[0].toUpperCase())
      setError({ type, description })
      onStepRun(!type && !description)
      if (!type && !description) {
        saveToLocalStorage({ completedStepIndex: stepIndex })
      }
    }

    document.addEventListener('editor-runStarted', handleRunStarted)
    document.addEventListener('editor-runCompleted', handleRunCompleted)

    // Remove listeners when the component unmounts
    return () => {
      document.removeEventListener('editor-runStarted', handleRunStarted)
      document.removeEventListener('editor-runCompleted', handleRunCompleted)
    }
  }, [codeTemplate, onStepRun, stepIndex])

  const runCode = () => {
    const selections = loadFromLocalStorage('selections') || {}
    // TODO: This is a bit of a hack, but it's the easiest way to get the active options
    const activeOptions = Object.fromEntries(
      Object.entries(options).filter(
        ([_key, option]) => option?.type !== undefined
      )
    )

    const error = validator(activeOptions, selections)
    if (Object.keys(error).length > 0) {
      setError(error)
      return
    }

    const webComponent = document.querySelector('editor-wc')
    webComponent.rerunCode()
  }

  useEffect(() => {
    setError({}) // Reset errors when the code changes

    if (codeTemplate) {
      setDisplayCode(
        getUserCodeForStep({
          template: codeTemplate,
          stepIndex,
          optionData: options,
        })
      )
    }
  }, [codeTemplate, options, stepIndex])

  return (
    <div className="c-step">
      <div className="c-step__inputs">
        <div className="c-step__instructions">
          <h2 className="rpf-squiggle-heading">
            {t('exercise.step.instructions')}
          </h2>
          <div
            className="c-step__instructions-content"
            dangerouslySetInnerHTML={{ __html: marked.parse(instructions) }}
          ></div>
        </div>
        <hr />
        <div className="c-step__options">
          <h2>{t('exercise.step.complete_the_code')}</h2>
          <Input
            onUserCodeUpdate={onUserCodeUpdate}
            template={displayCode}
            options={options}
            onCodeChange={() => onStepRun(false)}
          />
          {error.description && error.type && (
            <ErrorMessage message={error.description} title={error.type} />
          )}
          <RunBar
            exerciseId={exerciseId}
            onRun={runCode}
            onNextStep={onNextStep}
            complete={completed}
            isLastStep={isLastStep}
          />
        </div>
      </div>
      <div className="c-step__output">
        <h2 className="rpf-squiggle-heading">
          {t('exercise.step.code_output')}
        </h2>
        <Editor codeToRun={codeToRun} />
      </div>
    </div>
  )
}

Step.propTypes = {
  exerciseId: PropTypes.string,
  stepIndex: PropTypes.number,
  isLastStep: PropTypes.bool,
  instructions: PropTypes.string.isRequired,
  codeTemplate: PropTypes.string.isRequired,
  onNextStep: PropTypes.func.isRequired,
  onStepRun: PropTypes.func.isRequired,
  completed: PropTypes.bool.isRequired,
  options: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
}

export default withTranslation('translations')(Step)
